import { useChat } from '@ai-sdk/react';
import { useQueryClient } from '@tanstack/react-query';
import { TextStreamChatTransport } from 'ai';
import { eachDayOfInterval, format } from 'date-fns';
import { useState, useEffect, useMemo, useRef } from 'react';

import {
  parseAIPlannerMessages,
  convertStreamingMealsToPlan,
} from './ai-planner-utils';
import { apiRoutes } from '@/api/api-routes';
import {
  useAIPlannerStore,
  type AIPlannerPreferences,
  type MealPlan,
  type WeekDay,
  EATING_GOAL_API_MAP,
  DIET_TYPE_API_MAP,
  ALLERGY_API_MAP,
  DEFAULT_TIME_SLOT,
} from '@/store/ai-planner';

interface UseAIPlannerStreamProps {
  preferences: Partial<AIPlannerPreferences>;
  customerId?: string;
  cityId?: string;
  address?: { lat?: number; lng?: number };
  token?: string;
}

type StreamStatus = 'idle' | 'submitted' | 'streaming' | 'ready' | 'error';

// Helper to convert UI dates to API format
function generateDateArray(
  startDate: string,
  endDate: string,
  deliveryDays: WeekDay[]
): string[] {
  const start = new Date(startDate);
  const end = new Date(endDate);
  const allDays = eachDayOfInterval({ start, end });

  // Map weekday names to day of week numbers (0 = Sunday, 1 = Monday, etc.)
  const dayMap: Record<WeekDay, number> = {
    T2: 1, // Monday
    T3: 2,
    T4: 3,
    T5: 4,
    T6: 5,
  };

  const selectedDayNumbers = deliveryDays.map((d) => dayMap[d]);

  return allDays
    .filter((date) => selectedDayNumbers.includes(date.getDay()))
    .map((date) => format(date, 'yyyy-MM-dd'));
}

export function useAIPlannerStream({
  preferences,
  customerId,
  cityId,
  address,
  token,
}: UseAIPlannerStreamProps) {
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [currentPlan, setCurrentPlan] = useState<MealPlan | null>(null);
  const hasStartedRef = useRef(false);
  const queryClient = useQueryClient();

  // Validate required preferences
  const hasRequiredPreferences = Boolean(
    preferences.deliveryDays?.length &&
      preferences.startDate &&
      preferences.endDate
  );

  // Build request body
  const requestBody = useMemo(() => {
    if (!hasRequiredPreferences) return null;

    const dates = generateDateArray(
      preferences.startDate!,
      preferences.endDate!,
      preferences.deliveryDays!
    );

    return {
      user_id: customerId ?? 'anonymous',
      user_profile: {
        date: dates,
        prefer_time_slot: [DEFAULT_TIME_SLOT],
        ...(preferences.eatingGoals?.length && {
          dietary_goals: preferences.eatingGoals
            .map((g) => EATING_GOAL_API_MAP[g])
            .join(', '),
        }),
        ...(preferences.dietType && {
          dietary_preference: DIET_TYPE_API_MAP[preferences.dietType],
        }),
        ...(preferences.allergies?.length && {
          allergies: preferences.allergies.map((a) => ALLERGY_API_MAP[a]),
        }),
        ...(preferences.userNote && {
          user_note: preferences.userNote,
        }),
        ...(preferences.budget && {
          budget: preferences.budget,
        }),
      },
    };
  }, [preferences, customerId, hasRequiredPreferences]);

  const { messages, sendMessage } = useChat({
    transport: new TextStreamChatTransport({
      api:
        process.env.NEXT_PUBLIC_MEAL_PLANNER_API ??
        'https://sse.ahafood.ai/planning-meal/stream',
      prepareSendMessagesRequest: () => {
        return {
          body: requestBody ?? {},
          headers: {
            ...(token && {
              Authorization: `Bearer ${token}`,
            }),
            ...(cityId && { 'X-City-Id': cityId }),
            ...(address?.lat && { 'X-Latitude': String(address.lat) }),
            ...(address?.lng && { 'X-Longitude': String(address.lng) }),
          },
        };
      },
    }),
    onData: () => {
      setStatus('ready');
    },
    onFinish: () => {
      setStatus('ready');

      // Backend auto-saves during stream, just invalidate cache
      if (token) {
        void queryClient.invalidateQueries({
          queryKey: [apiRoutes.ahafood.store.meal_plans, token],
        });
      }
    },
    onError: (error) => {
      console.error('AI Planner streaming error:', error);
      setStatus('error');
    },
  });

  // Parse messages and update store
  useEffect(() => {
    if (messages.length === 0) return;

    const lastMessage = messages[messages.length - 1];
    if (lastMessage.role !== 'assistant') return;

    const parsed = parseAIPlannerMessages(lastMessage.parts);

    const {
      clearStreamingState,
      appendStreamingText,
      addStreamingMeal,
      setStreamingProgress,
      setStreamingMessages,
    } = useAIPlannerStore.getState();

    // Update streaming text
    if (parsed.answer) {
      clearStreamingState();
      appendStreamingText(parsed.answer);
    }

    // Update streaming meals
    if (parsed.streamingMeals.length > 0) {
      parsed.streamingMeals.forEach((meal) => {
        addStreamingMeal(meal);
      });

      // Calculate progress based on meals received
      const totalExpectedDays = preferences.deliveryDays?.length ?? 1;
      const uniqueDates = new Set(parsed.streamingMeals.map((m) => m.date));
      const progress = Math.min(
        100,
        Math.round((uniqueDates.size / totalExpectedDays) * 100)
      );
      setStreamingProgress(progress);

      // Convert to plan when complete
      if (progress >= 100) {
        const plan = convertStreamingMealsToPlan(
          parsed.streamingMeals,
          preferences
        );
        setCurrentPlan(plan);
      }
    }

    // Save messages to store for persistence
    setStreamingMessages(messages);
  }, [messages, preferences]);

  // Auto-start generation when component mounts
  useEffect(() => {
    if (
      hasRequiredPreferences &&
      messages.length === 0 &&
      status === 'idle' &&
      !hasStartedRef.current
    ) {
      hasStartedRef.current = true;
      useAIPlannerStore.getState().clearStreamingState();
      setStatus('streaming');
      // Send a dummy message to trigger the stream
      void sendMessage({
        role: 'user',
        parts: [{ type: 'text', text: 'Generate meal plan' }],
      });
    }
  }, [hasRequiredPreferences, messages.length, status, sendMessage]);

  return {
    messages,
    currentPlan,
    status,
    error:
      status === 'error' ? new Error('Failed to generate meal plan') : null,
  };
}
